import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { pollHealthUntilReady } from '../services/healthCheckService';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const WarmingOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  color: #e0e0e0;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const WarmingContent = styled.div`
  text-align: center;
  max-width: 360px;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 1.5rem;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #4a9eff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  margin: 0 0 0.5rem;
  color: #fff;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #a0a0a0;
  margin: 0;
`;

const AttemptInfo = styled.p`
  font-size: 0.8rem;
  color: #707070;
  margin-top: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: #ff8a80;
  margin-top: 1rem;
`;

const RetryButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: #4a9eff;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: #3a8eef;
  }
`;

/**
 * Gates the app until the backend health check passes.
 * Polls /health to warm up Cloud Run before showing the main UI.
 */
const BackendHealthGate = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const runHealthCheck = async () => {
    setError(null);
    setAttempt(0);
    setIsRetrying(true);

    const ready = await pollHealthUntilReady({
      intervalMs: 2500,
      maxWaitMs: 90000, // 90 seconds max for Cloud Run cold start
      onAttempt: (currentAttempt) => {
        setAttempt(currentAttempt);
      },
    });

    setIsRetrying(false);
    setIsReady(ready);

    if (!ready) {
      setError('Unable to reach server. Please check your connection and try again.');
    }
  };

  useEffect(() => {
    runHealthCheck();
  }, []);

  if (isReady) {
    return children;
  }

  return (
    <WarmingOverlay>
      <WarmingContent>
        <Spinner />
        <Title>Connecting to server</Title>
        <Subtitle>
          {isRetrying
            ? 'Checking connection…'
            : 'Starting up the backend. This may take a moment on first visit.'}
        </Subtitle>
        {attempt > 0 && (
          <AttemptInfo>Attempt {attempt} – warming up Cloud Run</AttemptInfo>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {error && (
          <RetryButton type="button" onClick={runHealthCheck}>
            Retry
          </RetryButton>
        )}
      </WarmingContent>
    </WarmingOverlay>
  );
};

export default BackendHealthGate;
